/**
 * Variable catalog — client-side mirror of
 * `apps/backend/src/libs/correspondence/variable-catalog.ts`. Keep the two in
 * lockstep when adding tokens.
 *
 * Drives the TipTap "Variable einfügen" picker, the plain-text variable
 * menus next to subject/preheader/button inputs, and the chip rendered for
 * each token inside the rich-text editor.
 */

export type VariableKind = 'recipient' | 'sender' | 'computed' | 'static';

export interface VariableDefinition {
  token: string;
  label: string;
  description: string;
  kind: VariableKind;
  multiline?: boolean;
}

export const EMAIL_VARIABLE_CATALOG: readonly VariableDefinition[] = [
  {
    token: 'anrede',
    label: 'Anrede',
    description:
      'Vollständige Anrede des Empfängers, abhängig von Geschlecht und Du/Sie.',
    kind: 'computed',
  },
  {
    token: 'kontakt.vorname',
    label: 'Empfänger – Vorname',
    description: 'Vorname des Empfängers.',
    kind: 'recipient',
  },
  {
    token: 'kontakt.nachname',
    label: 'Empfänger – Nachname',
    description: 'Nachname des Empfängers.',
    kind: 'recipient',
  },
  {
    token: 'kontakt.email',
    label: 'Empfänger – E-Mail',
    description: 'E-Mail-Adresse des Empfängers.',
    kind: 'recipient',
  },
  {
    token: 'firma',
    label: 'Firma',
    description: 'Name des Geschäftspartners.',
    kind: 'recipient',
  },
  {
    token: 'grussformel',
    label: 'Grußformel',
    description:
      'Vollständige Grußformel inkl. Absendername in zwei Zeilen.',
    kind: 'computed',
    multiline: true,
  },
  {
    token: 'absender.vorname',
    label: 'Absender – Vorname',
    description: 'Vorname des Absenders.',
    kind: 'sender',
  },
  {
    token: 'absender.nachname',
    label: 'Absender – Nachname',
    description: 'Nachname des Absenders.',
    kind: 'sender',
  },
  {
    token: 'absender.email',
    label: 'Absender – E-Mail',
    description: 'E-Mail-Adresse des Absenders.',
    kind: 'sender',
  },
  {
    token: 'datum',
    label: 'Heutiges Datum',
    description: 'Heutiges Datum (TT.MM.JJJJ).',
    kind: 'static',
  },
] as const;

export const EMAIL_VARIABLE_BY_TOKEN: ReadonlyMap<string, VariableDefinition> =
  new Map(EMAIL_VARIABLE_CATALOG.map((v) => [v.token, v]));

export function isKnownVariable(token: string): boolean {
  return EMAIL_VARIABLE_BY_TOKEN.has(token);
}
